import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Users, 
  Shield, 
  Eye, 
  Clock, 
  Search,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Mail,
  Chrome,
  Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UserSessions - Admin view for managing user sessions and impersonation
 * 
 * Enterprise-grade admin support mode:
 * - View all users with session information
 * - Start impersonation sessions (30-min timeout)
 * - Full audit logging
 * - RBAC enforced (super_admin only)
 */
const UserSessions = ({ apiClient, onImpersonate }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [impersonationReason, setImpersonationReason] = useState("");
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Load users with session data
  useEffect(() => {
    loadUserSessions();
  }, []);

  // Filter users based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user => 
      user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query) ||
      user.user_id?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const loadUserSessions = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/admin/users/sessions");
      setUsers(response.data.users || []);
      setFilteredUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to load user sessions:", error);
      toast.error("Failed to load user sessions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImpersonateClick = (user) => {
    setSelectedUser(user);
    setImpersonationReason("");
    setShowImpersonateDialog(true);
  };

  const startImpersonation = async () => {
    if (!selectedUser) return;
    
    setIsImpersonating(true);
    try {
      const response = await apiClient.post("/admin/impersonate", {
        target_user_id: selectedUser.user_id,
        reason: impersonationReason || "Support request"
      });

      toast.success(`Impersonation started for ${selectedUser.email}`);
      setShowImpersonateDialog(false);
      
      // Call the parent callback with impersonation data
      onImpersonate?.({
        impersonation_token: response.data.impersonation_token,
        impersonation_session_id: response.data.impersonation_session_id,
        target_user: response.data.target_user,
        expires_at: response.data.expires_at,
        timeout_minutes: response.data.timeout_minutes
      });

    } catch (error) {
      console.error("Failed to start impersonation:", error);
      toast.error(error.response?.data?.detail || "Failed to start impersonation");
    } finally {
      setIsImpersonating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              User Sessions & Support Access
            </CardTitle>
            <CardDescription>
              View user sessions and access accounts for support (30-min timeout, full audit logging)
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadUserSessions}
            disabled={isLoading}
          >
            <Activity className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by email, name, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="user-sessions-search"
          />
        </div>
      </CardHeader>

      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2">User</th>
                <th className="text-left py-3 px-2">Auth</th>
                <th className="text-left py-3 px-2">Role</th>
                <th className="text-left py-3 px-2">Sessions</th>
                <th className="text-left py-3 px-2">Last Login</th>
                <th className="text-left py-3 px-2">Status</th>
                <th className="text-left py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr 
                  key={user.user_id} 
                  className="border-b hover:bg-secondary/20"
                  data-testid={`user-session-row-${user.user_id}`}
                >
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      {user.picture && (
                        <img 
                          src={user.picture} 
                          alt="" 
                          className="w-6 h-6 rounded-full" 
                        />
                      )}
                      <div>
                        <p className="font-medium">{user.name || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <Badge 
                      variant="outline" 
                      className={user.auth_method === "google" ? "border-blue-500 text-blue-600" : "border-gray-400 text-gray-600"}
                    >
                      {user.auth_method === "google" ? (
                        <><Chrome className="w-3 h-3 mr-1" /> Google</>
                      ) : (
                        <><Mail className="w-3 h-3 mr-1" /> Email</>
                      )}
                    </Badge>
                  </td>
                  <td className="py-3 px-2">
                    <Badge 
                      variant={user.role === "admin" ? "destructive" : user.role === "premium" ? "default" : "secondary"}
                    >
                      {user.role}
                    </Badge>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-1">
                      <Activity className="w-3 h-3 text-green-500" />
                      <span>{user.active_sessions || 0} active</span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-muted-foreground text-xs">
                    {user.last_login_at 
                      ? new Date(user.last_login_at).toLocaleString()
                      : "Never"
                    }
                  </td>
                  <td className="py-3 px-2">
                    {user.is_being_impersonated ? (
                      <Badge variant="destructive" className="text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        Active Session
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Available
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 px-2">
                    {user.can_impersonate ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImpersonateClick(user)}
                        className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                        data-testid={`impersonate-btn-${user.user_id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Impersonate
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {user.role === "admin" ? "Admin user" : 
                         user.is_being_impersonated ? `By ${user.impersonated_by}` : "N/A"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No users found</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try a different search term</p>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Impersonation Confirmation Dialog */}
      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Start Support Session
            </DialogTitle>
            <DialogDescription>
              You are about to access this user's account for support purposes.
              This action is fully logged and audited.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 py-4">
              {/* User Info */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  {selectedUser.picture && (
                    <img 
                      src={selectedUser.picture} 
                      alt="" 
                      className="w-10 h-10 rounded-full"
                    />
                  )}
                  <div>
                    <p className="font-medium">{selectedUser.name || "Unknown"}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="text-sm font-medium">Reason for access (optional)</label>
                <Input
                  placeholder="e.g., Support ticket #123, debugging issue..."
                  value={impersonationReason}
                  onChange={(e) => setImpersonationReason(e.target.value)}
                  data-testid="impersonation-reason-input"
                />
              </div>

              {/* Warnings */}
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-amber-800">
                    <p className="font-medium">Important:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1 text-xs">
                      <li>Session expires in <strong>30 minutes</strong></li>
                      <li>All actions will be logged and cannot be edited</li>
                      <li>Sensitive operations (password change, billing) are blocked</li>
                      <li>A visible banner will indicate support mode</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowImpersonateDialog(false)}
              disabled={isImpersonating}
            >
              Cancel
            </Button>
            <Button
              onClick={startImpersonation}
              disabled={isImpersonating}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-impersonate-btn"
            >
              {isImpersonating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Start Support Session
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserSessions;
